import React from 'react';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Sparkles, 
  Star, 
  ShieldCheck, 
  MessageCircle, 
  Facebook, 
  Instagram,
  ArrowUpRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import logo from '../logo.png';

export default function Footer() {
  return (
    <footer className="bg-gradient-to-b from-[#041a0f] via-[#03140c] to-[#010604] text-white relative overflow-hidden border-t border-[#eddca6]/20 font-sans">
      
      {/* Soft Ambient Light Glow Blobs */}
      <div className="absolute top-0 left-1/4 -translate-x-1/2 w-[500px] h-[500px] bg-gradient-to-tr from-gold-900/10 to-transparent rounded-full filter blur-[140px] pointer-events-none"></div>
      <div className="absolute bottom-0 right-10 w-[350px] h-[350px] bg-[#eddca6]/5 rounded-full filter blur-[100px] pointer-events-none"></div>

      {/* Decorative Blinking Sparkles */}
      <div className="absolute top-10 right-[10%] pointer-events-none animate-pulse">
        <Sparkles className="w-8 h-8 text-[#eddca6]/40" />
      </div>
      <div className="absolute bottom-16 left-[5%] pointer-events-none animate-pulse">
        <Star className="w-7 h-7 text-[#eddca6]/30 fill-[#eddca6]/10" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-12 z-10 space-y-16">
        
        {/* Main Grid Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8">
          
          {/* Brand & Overview Column (4 cols) */}
          <div className="lg:col-span-4 space-y-6">
            <Link to="/" className="flex items-center group">
              <img 
                src={logo}
                alt="Chirayil Gold and Diamonds Logo"
                className="h-12 w-12 mr-3 transition-transform duration-500 group-hover:rotate-[360deg] brightness-0 invert"
              />
              <span className="flex flex-col">
                <span className="text-xl font-serif font-black tracking-widest text-[#eddca6] transition-all duration-300 group-hover:text-[#dfc27d]">
                  CHIRAYIL
                </span>
                <span className="text-[9px] uppercase tracking-[0.25em] font-medium text-white/70 -mt-1 font-sans">
                  Gold & Diamonds • Est. 1985
                </span>
                <span className="text-[9px] sm:text-[10px] uppercase tracking-wider font-semibold text-[#eddca6]/90 mt-0.5 font-sans">
                  A unit from Vaikom Manufacturer • Wholesale and Retail
                </span>
              </span>
            </Link>

            <p className="text-xs text-white/75 leading-relaxed font-light max-w-sm">
              A unit from Vaikom Manufacturer — Wholesale and Retail. Crafting Wayanad's legacy of gold purity, authentic Kerala bridal ornaments, and 100% BIS 916 hallmarked excellence for over four decades.
            </p>

            {/* Certification Badges */}
            <div className="pt-2 flex flex-wrap gap-3">
              <div className="inline-flex items-center gap-2 bg-[#0b3a24] border border-[#eddca6]/30 text-[#eddca6] px-3.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider">
                <ShieldCheck className="w-3.5 h-3.5 text-[#eddca6]" />
                <span>BIS 916 Hallmarked</span>
              </div>
              <div className="inline-flex items-center gap-2 bg-[#0b3a24] border border-[#eddca6]/30 text-[#eddca6] px-3.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider">
                <Sparkles className="w-3.5 h-3.5 text-[#eddca6]" />
                <span>GIA Certified</span>
              </div>
            </div>
          </div>

          {/* Quick Navigation Links (2 cols) */}
          <div className="lg:col-span-2 space-y-4">
            <h3 className="text-sm font-serif font-bold tracking-widest uppercase text-[#eddca6]">Navigation</h3>
            <ul className="space-y-2.5 text-xs text-white/80 font-light">
              <li>
                <Link to="/" className="hover:text-[#eddca6] transition-colors flex items-center gap-1 group">
                  <span>Home</span>
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-[#eddca6]" />
                </Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-[#eddca6] transition-colors flex items-center gap-1 group">
                  <span>About Us</span>
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-[#eddca6]" />
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-[#eddca6] transition-colors flex items-center gap-1 group">
                  <span>Contact Showroom</span>
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-[#eddca6]" />
                </Link>
              </li>
              <li>
                <Link to="/schemes" className="hover:text-[#eddca6] transition-colors flex items-center gap-1 group">
                  <span>Gold Savings Schemes</span>
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-[#eddca6]" />
                </Link>
              </li>
              <li>
                <Link to="/passbook" className="hover:text-[#eddca6] transition-colors flex items-center gap-1 group">
                  <span>Investment Passbook</span>
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-[#eddca6]" />
                </Link>
              </li>
            </ul>
          </div>

          {/* Showroom Details (3 cols) */}
          <div className="lg:col-span-3 space-y-4">
            <h3 className="text-sm font-serif font-bold tracking-widest uppercase text-[#eddca6]">Flagship Showroom</h3>
            <div className="space-y-3.5 text-xs text-white/80 font-light">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#eddca6] flex-shrink-0 mt-0.5" />
                <span>PERIKALLUR Road, Pulpally, Wayanad, Kerala - 673579</span>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-[#eddca6] flex-shrink-0 mt-0.5" />
                <div className="space-y-0.5">
                  <a href="tel:+919605155088" className="block hover:text-[#eddca6] transition-colors font-sans">
                    +91 9605155088
                  </a>
                  <a href="tel:+916282370250" className="block hover:text-[#eddca6] transition-colors font-sans">
                    +91 6282370250
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-[#eddca6] flex-shrink-0 mt-0.5" />
                <a href="mailto:info@chirayilgoldanddiamonds.com" className="hover:text-[#eddca6] transition-colors font-sans truncate">
                  info@chirayilgoldanddiamonds.com
                </a>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-[#eddca6] flex-shrink-0 mt-0.5" />
                <span>MON - SUN: 10:00 AM - 08:00 PM</span>
              </div>
            </div>
          </div>

          {/* Social Links & Assistance (3 cols) */}
          <div className="lg:col-span-3 space-y-5">
            <h3 className="text-sm font-serif font-bold tracking-widest uppercase text-[#eddca6]">Connect With Us</h3>
            <p className="text-xs text-white/70 font-light leading-relaxed">
              Follow our latest Kerala gold jewellery design launches & daily rate updates.
            </p>
            
            <div className="flex gap-3">
              <a 
                href="tel:+919605155088" 
                className="w-10 h-10 border border-[#eddca6]/30 bg-white/5 rounded-xl flex items-center justify-center hover:border-[#eddca6] hover:bg-[#eddca6] hover:text-[#041a0f] transition-all duration-300 group shadow-md"
                aria-label="Phone"
              >
                <Phone className="w-4 h-4 text-[#eddca6] group-hover:text-[#041a0f]" />
              </a>
              <a 
                href="https://wa.me/919605155088" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-[#eddca6]/30 bg-white/5 rounded-xl flex items-center justify-center hover:border-[#eddca6] hover:bg-[#eddca6] hover:text-[#041a0f] transition-all duration-300 group shadow-md"
                aria-label="WhatsApp"
              >
                <MessageCircle className="w-4 h-4 text-[#eddca6] group-hover:text-[#041a0f]" />
              </a>
              <a 
                href="https://facebook.com" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-[#eddca6]/30 bg-white/5 rounded-xl flex items-center justify-center hover:border-[#eddca6] hover:bg-[#eddca6] hover:text-[#041a0f] transition-all duration-300 group shadow-md"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4 text-[#eddca6] group-hover:text-[#041a0f]" />
              </a>
              <a 
                href="https://instagram.com" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-[#eddca6]/30 bg-white/5 rounded-xl flex items-center justify-center hover:border-[#eddca6] hover:bg-[#eddca6] hover:text-[#041a0f] transition-all duration-300 group shadow-md"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4 text-[#eddca6] group-hover:text-[#041a0f]" />
              </a>
            </div>

            <div className="pt-2 space-y-1 text-xs text-white/70">
              <p className="text-[10px] uppercase tracking-widest text-[#eddca6] font-bold">Showroom Support</p>
              <p className="text-[11px] font-light">Pulpally, Wayanad • Private Bridal Lounge</p>
            </div>
          </div>

        </div>

        {/* Metallic Gold Gradient Divider */}
        <div className="pt-8">
          <div className="h-px bg-gradient-to-r from-transparent via-[#eddca6]/40 to-transparent"></div>
        </div>

        {/* Bottom Copyright & Legal Links */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-white/60 font-light pt-2">
          <p className="text-center sm:text-left">
            © 2026 <span className="text-[#eddca6] font-bold">Chirayil Gold & Diamonds</span>. All rights reserved.
          </p>

          <div className="flex items-center gap-6 text-[11px]">
            <span className="text-white/20">•</span>
            <Link to="/privacy-policy" className="hover:text-[#eddca6] transition-colors">
              Privacy Policy
            </Link>
            <span className="text-white/20">•</span>
            <Link to="/terms-and-conditions" className="hover:text-[#eddca6] transition-colors">
              Terms & Conditions
            </Link>
            <span className="text-white/20">•</span>
            <Link to="/refund-policy" className="hover:text-[#eddca6] transition-colors">
              Refund Policy
            </Link>
          </div>
        </div>

      </div>
    </footer>
  );
}
